// src/paths/PathIntakeChat.tsx — the conversational intake that replaced the
// /paths form (Francis 2026-08-11). Many code learners can't compress a goal
// into one good prompt, so a counselor chat narrows it down (goal → hours →
// prior knowledge) and shows a proposal card; only the confirmed proposal
// reaches POST /api/paths.
import React, { useEffect, useRef, useState } from 'react';
import { pathsApi } from '../services/api';
import { IntakeMessage, PathProposal } from '../types/paths';

const GREETING =
  "What would you like to learn? Tell me in your own words — a topic, a goal, or even just \"I want to get into data jobs\". No perfect wording needed.";

const DURATION_CHIPS = ['1 hour', '2 hours', '5 hours', '10 hours', '20 hours'];

interface Props {
  building: boolean; // parent is running the 30s path generation
  onBuild: (proposal: PathProposal) => void;
}

const PathIntakeChat: React.FC<Props> = ({ building, onBuild }) => {
  const [messages, setMessages] = useState<IntakeMessage[]>([
    { role: 'assistant', content: GREETING },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState<PathProposal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Derived from messages (not a flag) so a failed send — which rolls the user
  // message back — automatically brings the chips back.
  const durationAnswered = messages.some(
    (m) => m.role === 'user' && DURATION_CHIPS.includes(m.content)
  );

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy, proposal]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || busy || building) return;
    setError(null);
    // A new message reopens the conversation — the old proposal is stale.
    setProposal(null);
    const next: IntakeMessage[] = [...messages, { role: 'user', content: trimmed }];
    setMessages(next);
    setInput('');
    setBusy(true);
    try {
      const res = await pathsApi.intake(next);
      setMessages([...next, { role: 'assistant', content: res.reply }]);
      if (res.ready && res.proposal) setProposal(res.proposal);
    } catch (err: any) {
      console.error('[PATHS-INTAKE] turn failed:', err);
      setError(err?.response?.data?.detail || 'Could not process that — please try again.');
      // Roll the user message back into the input so nothing is lost.
      setMessages(messages);
      setInput(trimmed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 flex flex-col">
      <div>
        <h2 className="text-base font-semibold text-foreground">Create a new path</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Tell us what you want to learn and how much time you have — we'll map the route,
          step by step, ending in a real project.
        </p>
      </div>

      {/* Conversation */}
      <div ref={scrollRef} className="mt-4 space-y-3 overflow-y-auto max-h-80 pr-1">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                m.role === 'user'
                  ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-br-md'
                  : 'bg-background border border-border text-foreground rounded-bl-md'
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="px-4 py-2.5 rounded-2xl rounded-bl-md bg-background border border-border">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-pulse"></span>
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-pulse" style={{ animationDelay: '150ms' }}></span>
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-pulse" style={{ animationDelay: '300ms' }}></span>
              </div>
            </div>
          </div>
        )}

        {/* Proposal card — the "concur" moment before the 30s generation */}
        {proposal && !busy && (
          <div className="border border-purple-300 dark:border-purple-700 rounded-2xl p-4 bg-purple-50/50 dark:bg-purple-900/20">
            <div className="text-sm font-semibold text-foreground">🧭 {proposal.topic}</div>
            <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
              <span className="px-2 py-1 rounded-lg bg-background border border-border">
                {proposal.duration_hours}h
              </span>
              {proposal.already_know && (
                <span className="px-2 py-1 rounded-lg bg-background border border-border">
                  skips: {proposal.already_know.slice(0, 60)}
                </span>
              )}
            </div>
            <button
              onClick={() => onBuild(proposal)}
              disabled={building}
              className="mt-3 px-5 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-blue-500 text-white text-sm font-semibold shadow-lg hover:from-purple-600 hover:to-blue-600 transition-all disabled:opacity-60 flex items-center gap-2"
            >
              {building ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Designing your path — ~30 seconds…
                </>
              ) : (
                <>✓ Build this path</>
              )}
            </button>
            <p className="text-xs text-muted-foreground mt-2">
              Not quite right? Just say what to change below.
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-3 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Duration quick replies — one tap instead of typing, hidden once answered */}
      {!durationAnswered && (
        <div className="flex flex-wrap gap-2 mt-4">
          {DURATION_CHIPS.map((chip) => (
            <button
              key={chip}
              type="button"
              disabled={busy || building}
              onClick={() => send(chip)}
              className="text-xs px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:border-purple-400 hover:text-purple-600 dark:hover:text-purple-300 transition-colors disabled:opacity-50"
            >
              {chip}
            </button>
          ))}
        </div>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); send(input); }}
        className="mt-3 flex gap-2"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your answer…"
          maxLength={2000}
          disabled={busy || building}
          className="flex-1 px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={!input.trim() || busy || building}
          className="px-5 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-blue-500 text-white text-sm font-semibold shadow hover:from-purple-600 hover:to-blue-600 transition-all disabled:opacity-50"
        >
          ➤
        </button>
      </form>
    </div>
  );
};

export default PathIntakeChat;
